import React, { useState } from "react";
import styles from "./CallScreen.module.css";

const DTMF_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const ActionBtn = ({ icon, label, active, disabled, onClick }) => (
  <button
    type="button"
    className={`${styles.actionBtn} ${active ? styles.actionActive : ""}`}
    disabled={disabled}
    onClick={onClick}
  >
    <span className={styles.actionIcon}>{icon}</span>
    <span className={styles.actionLabel}>{label}</span>
  </button>
);

const MicIcon = ({ off }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm6-3a6 6 0 0 1-12 0M12 18v3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    {off && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
  </svg>
);

const KeypadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    {[5, 12, 19].map((y) =>
      [5, 12, 19].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.8" />)
    )}
  </svg>
);

const HoldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <rect x="7" y="5" width="3.5" height="14" rx="1.2" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="1.2" />
  </svg>
);

const SpeakerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M4 9v6h4l5 4V5L8 9H4Z" fill="currentColor" />
    <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const EndCallIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M6.6 10.8c1.44 2.83 3.77 5.15 6.6 6.6l2.2-2.2c.28-.28.68-.36 1.03-.25 1.13.37 2.35.57 3.6.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.85 21 3 13.15 3 3.5 3 2.95 3.45 2.5 4 2.5H7.5c.55 0 1 .45 1 1 0 1.25.2 2.47.57 3.6.11.35.03.75-.25 1.03l-2.22 2.17Z"
      fill="#fff"
    />
  </svg>
);

const CallScreen = ({
  number,
  contactName,
  callerId,
  callState,
  duration,
  muted,
  held,
  speakerOn,
  onMute,
  onHold,
  onSpeaker,
  onHangUp,
  onKeypad,
}) => {
  const [showKeypad, setShowKeypad] = useState(false);
  const [dtmfInput, setDtmfInput] = useState("");
  // Naam dikh raha ho to number chhupa rehta hai — naam pe tap karke khulta hai.
  const [showNumber, setShowNumber] = useState(false);

  const isActive = callState === "active";
  const isBusy = callState === "busy";
  const isFailed = callState === "failed";

  const handleDtmf = (key) => {
    setDtmfInput((prev) => prev + key);
    onKeypad?.(key);
  };

  return (
    <div className={styles.screen}>
      <div className={styles.top}>
        <div className={styles.avatar}>
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,0.9)" />
            <path
              d="M4 20c0-3.31 3.58-6 8-6s8 2.69 8 6"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {contactName ? (
          <div className={styles.identity}>
            <button
              type="button"
              className={styles.nameBtn}
              onClick={() => setShowNumber((open) => !open)}
              aria-expanded={showNumber}
            >
              {contactName}
              <span className={`${styles.caret} ${showNumber ? styles.caretOpen : ""}`}>▾</span>
            </button>
            {showNumber ? (
              <div className={styles.numberDrop}>{number || "Unknown"}</div>
            ) : null}
          </div>
        ) : (
          <div className={styles.number}>{number || "Unknown"}</div>
        )}

        <div className={styles.statusRow}>
          {held ? (
            <span className={`${styles.badge} ${styles.hold}`}>On hold</span>
          ) : isBusy ? (
            <span className={`${styles.badge} ${styles.busy}`}>Line busy</span>
          ) : isFailed ? (
            <span className={`${styles.badge} ${styles.failed}`}>Call failed</span>
          ) : callState === "calling" ? (
            <span className={styles.badge}>
              <span className={styles.dot} />
              Calling…
            </span>
          ) : callState === "ringing" ? (
            <span className={styles.badge}>
              <span className={styles.dot} />
              Ringing…
            </span>
          ) : isActive ? (
            <span className={`${styles.badge} ${styles.live}`}>
              <span className={styles.liveDot} />
              {duration}
            </span>
          ) : null}
        </div>

        {callerId ? <p className={styles.callerId}>from {callerId}</p> : null}
      </div>

      {showKeypad ? (
        <div className={styles.dtmfArea}>
          <div className={styles.dtmfDisplay}>{dtmfInput || " "}</div>
          <div className={styles.dtmfGrid}>
            {DTMF_KEYS.map((k) => (
              <button key={k} type="button" className={styles.dtmfKey} onClick={() => handleDtmf(k)}>
                {k}
              </button>
            ))}
          </div>
        </div>
      ) : (
        !isBusy &&
        !isFailed && (
          <div className={styles.actionGrid}>
            <ActionBtn
              icon={<MicIcon off={muted} />}
              label={muted ? "Unmute" : "Mute"}
              active={muted}
              disabled={!isActive && !held}
              onClick={onMute}
            />
            <ActionBtn
              icon={<KeypadIcon />}
              label="Keypad"
              disabled={!isActive}
              onClick={() => setShowKeypad(true)}
            />
            <ActionBtn
              icon={<SpeakerIcon />}
              label="Speaker"
              active={speakerOn}
              onClick={onSpeaker}
            />
            <ActionBtn
              icon={<HoldIcon />}
              label={held ? "Resume" : "Hold"}
              active={held}
              disabled={!isActive && !held}
              onClick={onHold}
            />
          </div>
        )
      )}

      <div className={styles.bottomRow}>
        {showKeypad && (
          <button type="button" className={styles.backBtn} onClick={() => setShowKeypad(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5m0 0 7 7m-7-7 7-7"
                stroke="#24252c"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <button
          type="button"
          className={styles.hangupBtn}
          onClick={onHangUp}
          aria-label="End call"
        >
          <EndCallIcon />
        </button>
      </div>
    </div>
  );
};

export default CallScreen;
